/**
 * SubmissionViewerPage — continuous scrollable viewer for all documents
 * with a TOC sidebar, submission picker (with "Latest" default), and
 * scroll tracking.
 *
 * Ported from INDx UnifiedSubmissionViewer.js, adapted to Chrona styling.
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  FileText,
  ChevronDown,
  ExternalLink,
  CircleAlert,
  BookOpen,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { useAuth } from '@/lib/AuthContext';
import { Telemetry } from '@/lib/telemetry';
import { startTimer, endTimer } from '@/lib/telemetry-timers';
import { LoadingSpinner } from '@/components/shared/LoadingSpinner';
import { SharePointAccessModal } from '@/components/shared/SharePointAccessModal';
import { LazySection } from './LazySection';
import type { ExplorerSubmission } from '@/types/api';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface SectionEntry {
  key: string;
  ectdSection: string;
  sectionTitle: string;
  module: string;
  fileName: string;
  fileType: string;
  documentId: string;
  sequenceNumber: string;
  displaySequence: number;
  html?: string | null;
  pdfBlobUrl?: string;
  error?: string;
  loading?: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const LATEST = '__latest__';

function moduleLabel(mod: string): string {
  const labels: Record<string, string> = {
    M1: 'Module 1 — Administrative',
    M2: 'Module 2 — Summaries',
    M3: 'Module 3 — Quality',
    M4: 'Module 4 — Nonclinical',
    M5: 'Module 5 — Clinical',
  };
  return labels[mod] || `Module ${mod}`;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function SubmissionViewerPage() {
  const { activeProject } = useProject();
  const { user } = useAuth();

  const [submissions, setSubmissions] = useState<ExplorerSubmission[]>([]);
  const [selectedSubmission, setSelectedSubmission] = useState<string>(LATEST);
  const [sections, setSections] = useState<SectionEntry[]>([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [spoModalUrl, setSpoModalUrl] = useState<string | null>(null);

  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  // A2F-577: nonce bumped every time viewerSections lands new data. A
  // derived key built from `sections.map(s => s.documentId).join(',')`
  // was insufficient because switching from "Latest" to "Sub N" (where
  // N is the newest sub) returns the same documents in the same order,
  // so the key never changed, the content-loading effect never re-ran,
  // and every section sat on "Loading..." forever.
  const [contentLoadTick, setContentLoadTick] = useState(0);
  const contentRef = useRef<HTMLDivElement>(null);
  const tocRef = useRef<HTMLDivElement>(null);

  // A2F-647: synchronous mirror of `sections` so loadSection can read the
  // latest section data without depending on setState updater side effects
  // (those run later during render commit, not when setState is called).
  const sectionsRef = useRef<SectionEntry[]>([]);
  // A2F-647: synchronous in-flight set so concurrent loadSection calls
  // for the same index dedupe before any setState is queued.
  const inFlightRef = useRef<Set<number>>(new Set());

  /* ---- 1. Fetch submissions list ---- */

  useEffect(() => {
    if (!activeProject) return;
    setDataLoading(true);

    api.documents
      .explorer(activeProject.id)
      .then((data) => {
        setSubmissions(data.submissions);
      })
      .catch(() => {})
      .finally(() => setDataLoading(false));
  }, [activeProject]);

  /* ---- 2. Fetch lifecycle-resolved section list from backend ---- */

  useEffect(() => {
    if (!activeProject) return;

    // A2F-647: cancel-on-unmount so React.StrictMode's setup-cleanup-setup
    // pair doesn't apply the first fetch's result. Without this, the first
    // resolve commits a fresh sections array (resetting per-section
    // loading flags), the LazySection observers re-fire, and every doc
    // gets fetched twice.
    let cancelled = false;
    const submission = selectedSubmission === LATEST ? undefined : selectedSubmission;

    api.documents
      .viewerSections(activeProject.id, submission)
      .then((data) => {
        if (cancelled) return;
        const list: SectionEntry[] = data.map((d) => ({
          key: `${d.ectdSection}-${d.documentId}`,
          ectdSection: d.ectdSection,
          sectionTitle: d.sectionTitle,
          module: d.module,
          fileName: d.fileName,
          fileType: d.fileType,
          documentId: d.documentId,
          sequenceNumber: d.sequenceNumber,
          displaySequence: d.displaySequence ?? 0,
          loading: false,
        }));
        setSections(list);
        sectionRefs.current = new Array(list.length).fill(null);
        setActiveSectionIndex(0);
        setContentLoadTick((t) => t + 1);
        Telemetry.viewerOpened({ section_count: list.length, submission_seq: submission ?? 'latest' });
      })
      .catch(() => {
        if (cancelled) return;
        setSections([]);
      });

    return () => {
      cancelled = true;
    };
  }, [activeProject, selectedSubmission]);

  /* ---- 3. Per-section lazy fetch (A2F-647) ---- */

  // Keep sectionsRef in sync so loadSection can read the latest section
  // state synchronously without depending on a setState updater (those
  // run later during render commit, not when setState is called).
  useEffect(() => {
    sectionsRef.current = sections;
  }, [sections]);

  // Triggered by LazySection's IntersectionObserver as each section
  // enters the viewport, and by scrollToSection on TOC click.
  //
  // Race control is via inFlightRef (synchronous Set) — NOT via side
  // effects in setState updaters, since React 18 queues those updaters
  // and runs them later during reconciliation. Stale resolves after
  // the user switched submissions are caught by re-checking documentId
  // in setSections via sectionsRef before applying state, and the
  // cleanup-on-contentLoadTick effect revokes any blob URLs in state.
  const loadSection = useCallback(async (index: number) => {
    if (inFlightRef.current.has(index)) return;

    const section = sectionsRef.current[index];
    if (!section) return;
    if (section.loading || section.html !== undefined || section.error) return;

    const { documentId, fileType } = section;
    inFlightRef.current.add(index);

    setSections((prev) =>
      prev.map((s, i) => (i === index ? { ...s, loading: true } : s)),
    );

    const timerKey = `render_${documentId}`;
    try {
      if (fileType === 'docx') {
        startTimer(timerKey);
        const html = await api.documents.renderFragment(documentId);
        const durationMs = endTimer(timerKey);
        Telemetry.viewerSectionRendered({
          document_id: documentId,
          file_type: fileType,
          duration_ms: durationMs,
        });
        setSections((prev) =>
          prev.map((sec, i) =>
            i === index && sec.documentId === documentId
              ? { ...sec, html, loading: false }
              : sec,
          ),
        );
      } else if (fileType === 'pdf') {
        // Fetch PDF bytes with auth headers, then create a blob URL
        // for the iframe. Plain <iframe src="..."> can't set
        // Authorization headers.
        startTimer(timerKey);
        const pdfUrl = api.documents.renderUrl(documentId);
        const headers = await api.getAuthHeaders();
        const pdfRes = await fetch(pdfUrl, { headers });
        if (!pdfRes.ok) throw new Error(`PDF fetch failed: ${pdfRes.status}`);
        const blob = await pdfRes.blob();

        // Skip if the user switched submissions while we were fetching.
        const stillCurrent = sectionsRef.current[index];
        if (!stillCurrent || stillCurrent.documentId !== documentId) {
          endTimer(timerKey);
          return;
        }

        const blobUrl = URL.createObjectURL(blob);
        const durationMs = endTimer(timerKey);
        Telemetry.viewerSectionRendered({
          document_id: documentId,
          file_type: fileType,
          duration_ms: durationMs,
        });
        // The `applied`-flag pattern (read a let-set-from-updater
        // synchronously after setSections) was tried and reverted: React
        // queues functional updaters and runs them later during
        // reconciliation, so the synchronous check always saw applied=false
        // and revoked every URL before the iframe could load it.
        // The pre-blob-URL staleness check above is the primary guard;
        // any rare leak from a microtask race is bounded (one blob per
        // rapid switch) and accepted.
        setSections((prev) =>
          prev.map((sec, i) =>
            i === index && sec.documentId === documentId
              ? { ...sec, html: null, pdfBlobUrl: `${blobUrl}#navpanes=0`, loading: false }
              : sec,
          ),
        );
      } else {
        setSections((prev) =>
          prev.map((sec, i) =>
            i === index && sec.documentId === documentId
              ? {
                  ...sec,
                  html: null,
                  loading: false,
                  error: `Preview not available for .${fileType} files.`,
                }
              : sec,
          ),
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to render content.';
      endTimer(timerKey);
      Telemetry.viewerRenderFailed({ document_id: documentId, error_message: msg });
      setSections((prev) =>
        prev.map((sec, i) =>
          i === index && sec.documentId === documentId
            ? { ...sec, html: null, loading: false, error: msg }
            : sec,
        ),
      );
    } finally {
      inFlightRef.current.delete(index);
    }
  }, []);

  // A2F-647: on submission switch (contentLoadTick bumps) or unmount,
  // revoke any PDF blob URLs and clear the in-flight set so loadSection
  // calls for new sections at previously-occupied indices don't dedupe
  // against stale entries. Preserves the A2F-577 invalidation contract.
  // Reads sectionsRef.current directly — sync effect above keeps it
  // current with sections, and the file's policy is no side effects in
  // setSections updater bodies.
  useEffect(() => {
    return () => {
      for (const s of sectionsRef.current) {
        if (s.pdfBlobUrl) URL.revokeObjectURL(s.pdfBlobUrl);
      }
      inFlightRef.current.clear();
    };
  }, [contentLoadTick]);

  /* ---- 4. Scroll tracking ---- */

  const handleScroll = useCallback(() => {
    const container = contentRef.current;
    if (!container || sectionRefs.current.length === 0) return;

    const containerRect = container.getBoundingClientRect();
    const threshold = containerRect.top + 120;

    let active = 0;
    for (let i = sectionRefs.current.length - 1; i >= 0; i--) {
      const el = sectionRefs.current[i];
      if (el) {
        const rect = el.getBoundingClientRect();
        if (rect.top <= threshold) {
          active = i;
          break;
        }
      }
    }

    setActiveSectionIndex(active);
  }, []);

  useEffect(() => {
    const container = contentRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => container.removeEventListener('scroll', handleScroll);
    // Re-run when sections change so we attach the listener once the
    // scroll container has mounted with content.
  }, [handleScroll, sections]);

  useEffect(() => {
    const tocContainer = tocRef.current;
    if (!tocContainer) return;
    const activeEl = tocContainer.querySelector('[data-toc-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [activeSectionIndex]);

  // Prefetch a sliding window of sections ahead so PDFs are blob-fetched
  // (and their off-screen iframes have time to paint) before they enter
  // the viewport. Window size has to exceed the IntersectionObserver's
  // rootMargin reach — otherwise every advance of activeSectionIndex just
  // re-targets sections IO already loaded, and no new fetch fires until
  // the user has scrolled multiple sections past the IO buffer.
  // loadSection no-ops on out-of-range, in-flight, or already-loaded.
  useEffect(() => {
    for (let offset = 1; offset <= 5; offset++) {
      loadSection(activeSectionIndex + offset);
    }
  }, [activeSectionIndex, loadSection]);

  /* ---- 5. TOC click → scroll ---- */

  const scrollToSection = useCallback(
    (index: number) => {
      // A2F-647: kick off the fetch immediately so a click on a far-away
      // TOC entry doesn't wait for the scroll to land before fetching.
      // loadSection is idempotent so the IntersectionObserver firing
      // shortly after is a safe no-op.
      loadSection(index);
      const el = sectionRefs.current[index];
      const container = contentRef.current;
      if (el && container) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        container.scrollTo({
          top: container.scrollTop + (elRect.top - containerRect.top),
          behavior: 'smooth',
        });
      }
    },
    [loadSection],
  );

  /* ---- 6. Open in Word ---- */

  const openInWord = useCallback(async (documentId: string) => {
    try {
      const res = await api.documents.openLocal(documentId);
      if (res.status === 'redirect' && res.webUrl) {
        if (user?.authMethod === 'sso') {
          window.open(res.webUrl, '_blank');
        } else {
          setSpoModalUrl(res.webUrl);
        }
      }
    } catch { /* ignore */ }
  }, [user?.authMethod]);

  /* ---- Render ---- */

  if (dataLoading) return <LoadingSpinner message="Loading submissions..." />;

  if (!activeProject) {
    return (
      <div className="max-w-2xl mx-auto mt-20 text-center">
        <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-brand-charcoal mb-2">No Project Selected</h2>
        <p className="text-sm text-slate-500">Select a project to view submissions.</p>
      </div>
    );
  }

  if (submissions.length === 0) {
    return (
      <div className="max-w-2xl mx-auto mt-20 text-center">
        <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h2 className="text-lg font-bold text-brand-charcoal mb-2">No Submissions Found</h2>
        <p className="text-sm text-slate-500">
          No submissions have been ingested yet. Complete the project setup to scan your
          submission folders.
        </p>
      </div>
    );
  }

  const displayLabel =
    selectedSubmission === LATEST
      ? 'Latest'
      : `Submission ${parseInt(selectedSubmission, 10)}`;

  return (
    <div className="flex h-[calc(100vh-48px)] -mx-6 -mb-6">
      {/* ---- TOC Sidebar ---- */}
      <div className="w-60 shrink-0 border-r border-slate-200 bg-white flex flex-col overflow-hidden">
        {/* Submission picker */}
        <div className="px-4 py-3 border-b border-slate-200">
          <label className="block text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
            Submission
          </label>
          <div className="relative">
            <select
              value={selectedSubmission}
              onChange={(e) => setSelectedSubmission(e.target.value)}
              className="w-full appearance-none bg-white border border-slate-200 rounded-lg px-3 py-2 pr-8 text-sm text-brand-charcoal font-medium focus:outline-none focus:ring-2 focus:ring-brand-orange/30 focus:border-brand-orange cursor-pointer"
            >
              <option value={LATEST}>Latest</option>
              {/* A2F-645: sort defensively in render so the dropdown is
                  always strict descending by submission number, regardless
                  of backend ordering or selection-time state mutations.
                  Spread first to avoid mutating the source state. */}
              {[...submissions]
                .sort((a, b) => b.displaySequence - a.displaySequence)
                .map((sub) => (
                  <option key={sub.sequenceNumber} value={sub.sequenceNumber}>
                    Sub {sub.displaySequence}
                    {sub.submissionDate ? ` — ${sub.submissionDate}` : ''}
                  </option>
                ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Section list */}
        <div ref={tocRef} className="flex-1 overflow-y-auto py-2">
          {sections.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-slate-400">
              No documents in this submission.
            </div>
          )}

          {sections.map((section, index) => {
            const isActive = index === activeSectionIndex;
            // A2F-584: when a section has multiple documents, disambiguate
            // the TOC rows by appending " — {filename}" so every file is
            // reachable (the backend previously deduped to 1 doc/section).
            const sectionDocCount = sections.filter(
              (s) => s.ectdSection === section.ectdSection,
            ).length;
            const showFileName = sectionDocCount > 1;
            return (
              <button
                key={section.key}
                data-toc-active={isActive}
                onClick={() => scrollToSection(index)}
                className={`w-full text-left px-4 py-2 transition-colors ${
                  isActive
                    ? 'text-brand-orange font-medium bg-orange-50 border-l-2 border-brand-orange'
                    : 'text-slate-600 hover:bg-slate-50 border-l-2 border-transparent'
                }`}
              >
                <span
                  className={`block text-[10px] ${
                    isActive ? 'text-brand-orange' : 'text-slate-400'
                  }`}
                >
                  {section.ectdSection}
                </span>
                <span className="block text-sm leading-snug truncate">
                  {section.sectionTitle}
                  {showFileName && (
                    <span className="text-slate-400"> — {section.fileName}</span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        {/* Document count footer */}
        {sections.length > 0 && (
          <div className="px-4 py-2 border-t border-slate-200 text-[10px] text-slate-400">
            {sections.length} document{sections.length !== 1 ? 's' : ''}
          </div>
        )}
      </div>

      {/* ---- Content Area ---- */}
      <div className="flex-1 flex flex-col overflow-hidden bg-slate-50/30">
        {/* Toolbar */}
        <div className="shrink-0 px-6 py-2 bg-white border-b border-slate-200 flex items-center gap-4">
          <h1 className="text-sm font-bold text-brand-charcoal">Submission Viewer</h1>
          <span className="text-slate-300">|</span>
          <span className="text-sm text-slate-500">{displayLabel}</span>
          <div className="flex-1" />
          {sections.length > 0 && (
            <span className="text-xs text-slate-400">
              {sections.length} document{sections.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>

        {/* Scrollable document content */}
        <div ref={contentRef} className="flex-1 overflow-y-auto px-8 py-6">
          {sections.map((section, index) => {
            const idle =
              !section.loading &&
              section.html === undefined &&
              !section.error;
            return (
              <LazySection
                key={section.key}
                index={index}
                shouldLoad={idle}
                onLoad={loadSection}
                setRef={(el) => {
                  sectionRefs.current[index] = el;
                }}
                scrollRoot={contentRef.current}
              >
                {/* Section divider header */}
                <div
                  className={`flex items-center gap-3 py-3 px-4 bg-slate-50 border border-slate-200 rounded-lg mb-4 ${
                    index === 0 ? '' : 'mt-8'
                  }`}
                >
                  <span className="text-brand-orange font-semibold text-sm">
                    {section.ectdSection}
                  </span>
                  <span className="text-sm font-medium text-brand-charcoal flex-1">
                    {section.sectionTitle}
                  </span>
                  <span className="text-[10px] text-slate-400 uppercase font-medium">
                    {moduleLabel(section.module)}
                  </span>
                  {selectedSubmission === LATEST && (
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold border border-slate-200 text-slate-500">
                      Sub {section.displaySequence}
                    </span>
                  )}
                  <button
                    onClick={() => openInWord(section.documentId)}
                    className="flex items-center gap-1 text-xs text-slate-400 hover:text-brand-orange transition-colors"
                    title="Open in Word"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    <span>Open</span>
                  </button>
                </div>

                {/* Content */}
                <div className="bg-white rounded-lg border border-slate-200 px-10 py-8 mb-2">
                  {idle && (
                    <div className="min-h-[400px] flex flex-col items-center justify-center text-center text-sm text-slate-300">
                      <FileText className="w-6 h-6 text-slate-200 mb-2" />
                      <span className="text-slate-400">{section.fileName}</span>
                    </div>
                  )}

                  {section.loading && (
                    <div className="min-h-[400px] flex flex-col items-center justify-center text-center text-sm text-slate-400">
                      <FileText className="w-6 h-6 text-slate-300 mb-2" />
                      Loading...
                    </div>
                  )}

                  {section.error && (
                    <div className="py-8 text-center">
                      <CircleAlert className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                      <p className="text-sm text-slate-500">{section.error}</p>
                      <button
                        onClick={() => openInWord(section.documentId)}
                        className="mt-2 text-xs text-brand-orange hover:underline"
                      >
                        Open in Word instead
                      </button>
                    </div>
                  )}

                  {section.html && (
                    <div
                      className="max-w-[850px] mx-auto"
                      style={{
                        fontFamily:
                          "'Public Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                      }}
                    >
                      <div
                        className="prose prose-sm max-w-none
                          prose-headings:text-brand-charcoal
                          prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-8
                          prose-h2:text-xl prose-h2:mb-3 prose-h2:mt-6
                          prose-h3:text-lg prose-h3:mb-2 prose-h3:mt-4
                          prose-p:text-sm prose-p:leading-relaxed prose-p:text-slate-700
                          prose-table:border-collapse prose-table:w-full
                          prose-td:border prose-td:border-slate-200 prose-td:px-3 prose-td:py-2 prose-td:text-sm
                          prose-th:border prose-th:border-slate-200 prose-th:px-3 prose-th:py-2 prose-th:text-sm prose-th:bg-slate-50 prose-th:font-semibold
                          prose-strong:text-brand-charcoal
                          prose-a:text-brand-orange prose-a:no-underline hover:prose-a:underline
                          prose-li:text-sm prose-li:text-slate-700
                        "
                        dangerouslySetInnerHTML={{ __html: section.html }}
                      />
                    </div>
                  )}

                  {section.pdfBlobUrl && (
                    <iframe
                      src={section.pdfBlobUrl}
                      style={{ width: '100%', height: '80vh', border: 'none' }}
                      title={section.sectionTitle || 'PDF document'}
                    />
                  )}

                  {section.html === null && !section.pdfBlobUrl && !section.error && (
                    <div className="py-8 text-center text-sm text-slate-400">
                      <FileText className="w-6 h-6 text-slate-300 mx-auto mb-2" />
                      No content extracted.
                    </div>
                  )}
                </div>
              </LazySection>
            );
          })}

          {sections.length > 0 && <div className="h-[50vh]" />}
        </div>
      </div>
      {spoModalUrl && (
        <SharePointAccessModal
          webUrl={spoModalUrl}
          onClose={() => setSpoModalUrl(null)}
        />
      )}
    </div>
  );
}
